/**
 * BLRRD paint engine — Dodge / Burn (lighten / darken) (Phase 4.C.paint.2,
 * Wave 9.2).
 *
 * Multiplicative exposure adjust on the layer pixels under the brush
 * (dossier §2.6):
 *
 *   α      = mask · exposureStrength · flow
 *   dodge:  factor = 1 + α      (lighten)
 *   burn:   factor = 1 − α      (darken)
 *   rgb   *= factor
 *
 * The multiplicative form preserves edges (no blur) — the "sharpness
 * preservation" the user asked for needs no extra work. Exposure strength
 * maps the UI 0..1 slider to ±0.5 max (≈ ±0.6 EV) so white pixels never
 * blow to NaN.
 *
 * Optional tonal-range gating (Shadows / Midtones / Highlights) multiplies
 * the per-pixel effect by a smoothstep band centred on the pixel's
 * luminance, so e.g. "Dodge Highlights" only lightens already-bright
 * pixels (dossier §2.6). The bands are evaluated on a perceptual log
 * (ACEScct-like) luminance so they feel uniform across the tonal range.
 * Range 'all' (default) applies everywhere.
 *
 * Provenance (clean-room): darkroom dodge/burn analogue (classic
 * photographic technique); luminance + smoothstep bands are textbook. NO
 * GIMP/GEGL source consulted.
 *
 * @module src/pipeline/paint/dodge-burn
 */

import { dabBounds, boundsToRegion, worldToMaskCell, sampleMaskBilinear } from './brush-ops.js';

/* Max multiplicative strength at slider = 1.0 → ±0.5 (≈ ±0.6 EV). */
export const DODGE_BURN_MAX_STRENGTH = 0.5;

/* Rec.709-ish luminance weights (ACEScg is a wide-gamut linear space; the
 * weights are close enough for a perceptual gate and avoid importing the
 * full colour matrix). */
const LUMA_R = 0.2722287168;   /* AP1 → Y (ACES) coefficients */
const LUMA_G = 0.6740817658;
const LUMA_B = 0.0536895174;

/* Tonal-band centres + widths in normalised log-luminance [0, 1]. */
const BAND_CENTRES = { shadows: 0.18, midtones: 0.5, highlights: 0.82 };
const BAND_WIDTH = 0.35;

/**
 * Map scene-linear luminance to a normalised perceptual position in [0, 1]
 * via a log2 curve clamped to a sensible exposure window (−8…+2 stops
 * around mid-grey 0.18). Below the floor → 0, above the ceiling → 1.
 */
function perceptualPos(luma) {
    if (!(luma > 0)) return 0;
    const stops = Math.log2(luma / 0.18);   /* 0 at mid-grey */
    const lo = -6, hi = 2;
    let t = (stops - lo) / (hi - lo);
    return t < 0 ? 0 : t > 1 ? 1 : t;
}

/**
 * Smoothstep band weight for a perceptual position `p` against a centre
 * `c` with half-width `BAND_WIDTH`. Returns 1 at the centre, 0 beyond the
 * width, smooth in between.
 */
function bandWeight(p, c) {
    const d = Math.abs(p - c) / BAND_WIDTH;
    if (d >= 1) return 0;
    const u = 1 - d;                 /* 1 at centre, 0 at edge */
    return u * u * (3 - 2 * u);      /* smoothstep */
}

/**
 * Resolve the tonal gate for a pixel luminance given a range name. 'all'
 * (or unknown) → 1 everywhere.
 *
 * @param {string} range  'all' | 'shadows' | 'midtones' | 'highlights'
 * @param {number} luma   scene-linear luminance
 * @returns {number} gate ∈ [0, 1]
 */
export function tonalGate(range, luma) {
    if (!range || range === 'all') return 1;
    const c = BAND_CENTRES[range];
    if (c == null) return 1;
    return bandWeight(perceptualPos(luma), c);
}

/**
 * Dodge/Burn dab.
 *
 * @param {Float32Array} rgba   active-layer buffer (mutated in place)
 * @param {number} w
 * @param {number} h
 * @param {number} cx
 * @param {number} cy
 * @param {object} brush
 * @param {Float32Array} brush.mask
 * @param {number} brush.maskSize
 * @param {number} brush.effSize
 * @param {number} brush.flow
 * @param {number} brush.strength    UI exposure 0..1 (mapped to ±0.5)
 * @param {boolean} brush.burn       true = darken, false = lighten
 * @param {string} [brush.range='all']  tonal gate
 * @returns {{x:number,y:number,w:number,h:number}|null} dirty region
 */
export function stampDodgeBurnDab(rgba, w, h, cx, cy, brush) {
    const { mask, maskSize, effSize, flow, burn } = brush;
    const b = dabBounds(cx, cy, effSize, w, h);
    if (!b) return null;
    const f = flow == null ? 1 : (flow < 0 ? 0 : flow > 1 ? 1 : flow);
    if (f <= 0) return null;
    const strength = clamp01(brush.strength == null ? 0.5 : brush.strength) * DODGE_BURN_MAX_STRENGTH;
    if (strength <= 0) return boundsToRegion(b);
    const range = brush.range || 'all';
    const r = effSize / 2;
    const ox = cx - r, oy = cy - r;

    for (let y = b.y0; y <= b.y1; y++) {
        const my = worldToMaskCell(y + 0.5, oy, effSize, maskSize);
        const row = y * w * 4;
        for (let x = b.x0; x <= b.x1; x++) {
            const mx = worldToMaskCell(x + 0.5, ox, effSize, maskSize);
            const cov = sampleMaskBilinear(mask, maskSize, mx, my);
            if (cov <= 0) continue;
            const j = row + x * 4;
            const pr = rgba[j], pg = rgba[j + 1], pb = rgba[j + 2];
            /* Tonal gate from the pixel's own luminance. */
            const luma = pr * LUMA_R + pg * LUMA_G + pb * LUMA_B;
            const gate = tonalGate(range, luma);
            const a = cov * strength * f * gate;
            if (a <= 0) continue;
            const factor = burn ? (1 - a) : (1 + a);
            rgba[j]     = pr * factor;
            rgba[j + 1] = pg * factor;
            rgba[j + 2] = pb * factor;
            /* Alpha untouched — dodge/burn changes exposure, not coverage. */
        }
    }
    return boundsToRegion(b);
}

function clamp01(v) {
    if (!Number.isFinite(v)) return 0;
    return v < 0 ? 0 : v > 1 ? 1 : v;
}

export { LUMA_R, LUMA_G, LUMA_B, BAND_CENTRES, BAND_WIDTH };
